
import React, { memo, useMemo, useCallback, useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { parseYYYYMM } from "../utils/helpers";

// Optimized Chart component with lazy loading and sampling
// For Feature 4, uses RegionID matching only (no name matching)
const OptimizedChart = memo(({ chartData, selectedMetro, tpByMetro, tpByRegionId, selectedRegionId, isLoading: externalIsLoading, areaType, title, height, noMargins = false }) => {
  const [optimizedData, setOptimizedData] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Initialize chart optimizer
  useEffect(() => {
    if (!window.SimpleChartOptimizer) {
      console.warn('SimpleChartOptimizer not loaded');
      return;
    }
    
    window.chartOptimizer = window.chartOptimizer || new window.SimpleChartOptimizer();
  }, []);

  // Optimize chart data when it changes
  useEffect(() => {
    console.log(`[OPTIMIZED_CHART] Chart data received:`, { 
      chartDataLength: chartData?.length, 
      chartData: chartData?.slice(0, 3),
      selectedMetro,
      areaType,
      timestamp: new Date().toISOString()
    });
    
    if (!chartData || chartData.length === 0) {
      console.log(`[OPTIMIZED_CHART] No chart data, setting empty array`);
      setOptimizedData([]);
      return;
    }

    const optimizeData = async () => {
      setInternalLoading(true);
      
      try {
        // Use the chartData prop directly for better compatibility
        const sampled = sampleData(chartData, 500);
        console.log(`[OPTIMIZED_CHART] Sample data result:`, {
          originalLength: chartData.length,
          sampledLength: sampled.length,
          sampleDataSample: sampled.slice(0, 3)
        });
        setOptimizedData(sampled);
        console.log(`[OPTIMIZED_CHART] Chart data optimized:`, sampled.length, 'points');
      } catch (error) {
        console.error('Error optimizing chart data:', error);
        setOptimizedData(chartData);
      } finally {
        setInternalLoading(false);
      }
    };

    optimizeData();
  }, [chartData, selectedMetro]);

  // Simple data sampling fallback
  const sampleData = useCallback((data, maxPoints) => {
    if (!data || data.length <= maxPoints) {
      console.log(`[OPTIMIZED_CHART] No sampling needed: ${data?.length || 0} <= ${maxPoints}`);
      return data;
    }
    
    console.log(`[OPTIMIZED_CHART] Sampling ${data.length} points to ${maxPoints}`);
    
    // For neighborhoods, use a more conservative sampling to avoid gaps
    const step = Math.ceil(data.length / maxPoints);
    const sampled = [];
    
    // Always include the first point
    sampled.push(data[0]);
    
    // Sample evenly but ensure we don't create gaps
    for (let i = step; i < data.length - step; i += step) {
      sampled.push(data[i]);
    }
    
    // Always include the last point
    if (sampled[sampled.length - 1] !== data[data.length - 1]) {
      sampled.push(data[data.length - 1]);
    }
    
    console.log(`[OPTIMIZED_CHART] Sampled ${data.length} points to ${sampled.length}`);
    console.log(`[OPTIMIZED_CHART] Sample data range:`, {
      first: sampled[0],
      last: sampled[sampled.length - 1],
      zeroCount: sampled.filter(d => d.price === 0 || d.price === null).length
    });
    
    return sampled;
  }, []);

  // Memoize expensive calculations
  const chartConfig = useMemo(() => {
    // Feature 4: Use RegionID lookup ONLY (no name matching)
    // Other features: Use name-based lookup
    let tpData = null;
    let tpTickX = null;
    
    // Helper function to find the best matching date in chart data for TP date
    const findMatchingDate = (tpDateStr, dataArray) => {
      if (!tpDateStr || !dataArray || dataArray.length === 0) return null;
      
      // Parse TP date (expecting YYYY-MM format)
      const tpYearMonth = parseYYYYMM(tpDateStr); // "2005-06"
      if (!tpYearMonth || tpYearMonth.length < 7) return null;
      
      // Extract year and month from TP date
      const [tpYear, tpMonth] = tpYearMonth.split('-').map(Number);
      if (!tpYear || !tpMonth) return null;
      
      // Try exact match first
      const exactMatch = dataArray.find(d => d.date === tpYearMonth);
      if (exactMatch) {
        console.log(`[OPTIMIZED_CHART] Exact date match found: ${tpYearMonth}`);
        return exactMatch.date;
      }
      
      // Find all dates that match the year and month
      const matchingDates = dataArray.filter(d => {
        const chartDate = String(d.date || '');
        if (!chartDate || chartDate.length < 7) return false;
        
        // Parse chart date (could be YYYY-MM-DD or YYYY-MM)
        const parts = chartDate.split('-');
        if (parts.length < 2) return false;
        
        const chartYear = parseInt(parts[0], 10);
        const chartMonth = parseInt(parts[1], 10);
        
        return chartYear === tpYear && chartMonth === tpMonth;
      });
      
      if (matchingDates.length === 0) {
        console.log(`[OPTIMIZED_CHART] No dates found matching ${tpYearMonth}`);
        // Debug: show nearby dates
        const nearbyDates = dataArray.slice(0, 20).map(d => d.date);
        console.log(`[OPTIMIZED_CHART] Sample dates in chart data:`, nearbyDates);
        return null;
      }
      
      console.log(`[OPTIMIZED_CHART] Found ${matchingDates.length} date(s) matching ${tpYearMonth}:`, matchingDates.map(d => d.date));
      
      // If multiple dates in the same month, prefer:
      // 1. First day of the month (most representative of the month)
      // 2. Or closest to the 1st (turn of the month)
      // 3. Or closest to mid-month (15th) as fallback
      if (matchingDates.length === 1) {
        console.log(`[OPTIMIZED_CHART] Using single matching date: ${tpYearMonth} -> ${matchingDates[0].date}`);
        return matchingDates[0].date;
      }
      
      // Multiple dates in the same month - prefer the 1st of the month, then closest to 1st
      let bestMatch = matchingDates[0];
      let bestPriority = Infinity;
      
      matchingDates.forEach(d => {
        const chartDate = String(d.date || '');
        const parts = chartDate.split('-');
        if (parts.length >= 3) {
          const day = parseInt(parts[2], 10);
          // Priority: 1st day = 0, 2nd = 1, ... 15th = 14, then higher days
          // Prefer earlier in the month for turning point representation
          const priority = day === 1 ? 0 : (day <= 15 ? day - 1 : day + 14);
          if (priority < bestPriority) {
            bestPriority = priority;
            bestMatch = d;
          }
        } else {
          // Date without day component - use as is if it's the only one or first
          if (matchingDates.length === 1 || bestMatch === matchingDates[0]) {
            bestMatch = d;
          }
        }
      });
      
      console.log(`[OPTIMIZED_CHART] Selected best match from ${matchingDates.length} dates: ${tpYearMonth} -> ${bestMatch.date}`);
      return bestMatch.date;
    };
    
    if (tpByRegionId && selectedRegionId) {
      // Feature 4: RegionID matching only
      tpData = tpByRegionId[selectedRegionId];
      
      // CRITICAL DEBUG: Log all available TP data to verify correct RegionID match
      console.error(`[OPTIMIZED_CHART] 🔍 TP DATA DEBUG FOR RegionID ${selectedRegionId}:`, {
        selectedRegionId,
        tpDataFound: !!tpData,
        tpData: tpData,
        allTpByRegionIdKeys: Object.keys(tpByRegionId || {}).slice(0, 10),
        tpByRegionIdSample: Object.entries(tpByRegionId || {}).slice(0, 3).map(([rid, data]) => ({
          regionId: rid,
          tp_date: data?.tp_date,
          score: data?.score
        })),
        selectedMetro,
        areaType
      });
      
      // CRITICAL: Only render TP if tp_found is true (floor rule must pass)
      // If floor rule failed, tpByRegionId entry is deleted, so this won't run
      // But add extra safety check
      const shouldRenderTP = tpData?.tp_date && optimizedData.length > 0;
      
      if (shouldRenderTP) {
        // Find matching date in chart data
        tpTickX = findMatchingDate(tpData.tp_date, optimizedData);
        console.error(`[OPTIMIZED_CHART] ✅ TP DATE FOUND - RegionID ${selectedRegionId}:`, {
          selectedRegionId,
          tpDate: tpData.tp_date,
          tpTickX,
          chartDataDates: {
            first: optimizedData[0]?.date,
            last: optimizedData[optimizedData.length - 1]?.date,
            totalPoints: optimizedData.length
          },
          willRenderReferenceLine: !!tpTickX,
          note: 'TP passed floor rule validation'
        });
      } else {
        if (!tpData?.tp_date) {
          console.log(`[OPTIMIZED_CHART] Feature 4 - No TP date found for RegionID:`, selectedRegionId);
        } else {
          console.log(`[OPTIMIZED_CHART] Feature 4 - No chart data available for date matching`);
        }
      }
    } else if (tpByMetro && selectedMetro) {
      // Other features: Name-based lookup (fallback)
      tpData = tpByMetro[selectedMetro];
      if (tpData?.tp_date && optimizedData.length > 0) {
        tpTickX = findMatchingDate(tpData.tp_date, optimizedData);
        console.log(`[OPTIMIZED_CHART] Name-based TP Date Check:`, {
          selectedMetro,
          hasTpByMetro: !!tpByMetro,
          tpData,
          tpTickX,
          willRenderReferenceLine: !!tpTickX
        });
      }
    }

    return {
      tpTickX,
      tpData,
      margin: { top: 20, right: 12, bottom: 50, left: 8 }
    };
  }, [selectedMetro, selectedRegionId, tpByMetro, tpByRegionId, optimizedData]);

  // Show loading indicator for external loading (from useChart) or internal loading
  if (externalIsLoading || internalLoading) {
    return (
      <div className="chart-container" style={{ marginTop: "30px", textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "18px", color: "#666", marginBottom: "10px" }}>
          🧠 Thinking...
        </div>
        <div style={{ fontSize: "14px", color: "#999" }}>
          Processing {selectedMetro ? selectedMetro : 'data'}...
        </div>
        <div style={{ fontSize: "12px", color: "#ccc", marginTop: "10px" }}>
          This may take a few seconds for large datasets
        </div>
      </div>
    );
  }

  if (!optimizedData || optimizedData.length === 0) {
    console.log(`[OPTIMIZED_CHART] No optimized data to render:`, { 
      optimizedData, 
      chartDataLength: chartData?.length,
      optimizedDataLength: optimizedData?.length,
      chartDataSample: chartData?.slice(0, 3)
    });
    
    // Check if this is a "data not available" case
    if (title && title.includes('(Data Not Available)')) {
      return (
        <div className="chart-container" style={{ marginTop: "30px", textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "18px", color: "#e74c3c", marginBottom: "10px" }}>
            📊 {title}
          </div>
          <div style={{ fontSize: "14px", color: "#666" }}>
            This metric is not available for this level of data
          </div>
        </div>
      );
    }
    
    return null;
  }
  
  console.log(`[OPTIMIZED_CHART] Rendering chart with ${optimizedData.length} data points`);
  
  // Debug: Log the optimized data structure
  console.log(`[OPTIMIZED_CHART] About to render chart with optimizedData:`, {
    optimizedDataLength: optimizedData.length,
    optimizedDataSample: optimizedData.slice(0, 3),
    title,
    selectedMetro
  });

  const wrapperStyle = {
    marginTop: noMargins ? 0 : "30px",
    marginBottom: noMargins ? 0 : "50px",
    // Only set inline height when explicitly provided.
    // Otherwise, let `.chart-container` CSS control the height
    ...(height ? { height } : {})
  };

  return (
    <div className="chart-container" style={wrapperStyle}>
      {/* Chart Title - left aligned */}
      {title && (
        <div style={{
          fontSize: "18px",
          fontWeight: "bold",
          color: "#2c3e50",
          marginBottom: "5px",
          textAlign: "left"
        }}>
          {title}
        </div>
      )}
      
      {/* Data points info - underneath title */}
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
        <span>Showing {optimizedData.length} data points</span>
      </div>
      
      {/* TP Date Display - if available */}
      {/* Feature 4 uses RegionID, other features use name */}
      {chartConfig.tpData?.tp_date ? (
        <div style={{
          fontSize: "12px",
          color: "#333",
          marginBottom: "6px",
          fontWeight: "500"
        }}>
          Market Turning Point: {parseYYYYMM(chartConfig.tpData.tp_date)}
        </div>
      ) : null}
      
      {/* Range dates - left aligned */}
      {optimizedData.length ? (
        <div style={{
          fontSize: "12px",
          color: "#666",
          marginBottom: "15px",
          textAlign: "left"
        }}>
          Range: {optimizedData[0]?.date} → {optimizedData[optimizedData.length - 1]?.date}
        </div>
      ) : null}
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart 
          data={optimizedData} 
          margin={chartConfig.margin}
        >
          <XAxis 
            dataKey="date" 
            tickFormatter={(value) => {
              // Format date from YYYY-MM-DD to YYYY-MM
              if (!value) return '';
              const dateStr = String(value).trim();
              return dateStr.length >= 7 ? dateStr.substring(0, 7) : dateStr;
            }}
            tickMargin={10}
            padding={{ left: 10, right: 10 }}
            interval="preserveStartEnd"
            tickLine={false}
            tick={{ dy: 10 }}
          />
          <YAxis />
          <Tooltip />
          {/* TP Reference Line - Shows turning point marker on chart */}
          {(() => {
            const shouldRender = !!chartConfig.tpTickX;
            console.log(`[OPTIMIZED_CHART] ReferenceLine render check:`, {
              shouldRender,
              tpTickX: chartConfig.tpTickX,
              hasOptimizedData: optimizedData.length > 0,
              sampleDates: optimizedData.slice(0, 3).map(d => d.date)
            });
            return shouldRender ? (
              <ReferenceLine
                x={chartConfig.tpTickX}
                stroke="#444"
                strokeDasharray="3 3"
                label={{ value: "TP", position: "insideTop", fill: "#333" }}
              />
            ) : null;
          })()}
          <Area 
            type="monotone" 
            dataKey="price" 
            stroke="#8884d8" 
            fill="#b6a7e5" 
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

OptimizedChart.displayName = 'OptimizedChart';

export default OptimizedChart;
